import React, { useState, useEffect, useRef } from 'react';
import EventSystem from './EventSystem';
import moment from 'moment';
import _ from 'lodash';
import { Tile, Body } from './style/tile.style';

export default function ScheduleDisplayModule({ parameters, onSendMessage, enableModuleLogging, addLogEntry }) {
    const [containerHeight, setContainerHeight] = useState(100);
    const [containerWidth, setContainerWidth] = useState(100);

    const scheduleDisplayRef = useRef(null);
    const headingCanvasRef = useRef(null);
    const bodyCanvasRef = useRef(null);
    const fontSizeCheckerRef = useRef(null);
    const liveRef = useRef({ containerHeight: 100, containerWidth: 100, lastDataMessage: { event_grouping_seq: 'xx99' } });

    useEffect(() => {
        enableModuleLogging();

        const h = parameters.containerHeight.container - 2;
        const w = parseInt(scheduleDisplayRef.current.offsetWidth - 10, 10);
        setContainerHeight(h);
        setContainerWidth(w);
        liveRef.current.containerHeight = h;
        liveRef.current.containerWidth = w;

        onSendMessage({
            sourceModuleName: parameters.config.module_name,
            event_grouping_seq: parameters.config.event_grouping_seq,
            customerID: parameters.userProfile.customer_id,
            siteID: parameters.userProfile.site_id,
            eventID: parameters.config.event_id,
        });

        EventSystem.subscribe('ScheduleControlModule', showMessage);

        const debouncedResize = _.debounce(screenResized, 400);
        window.addEventListener('resize', debouncedResize);

        return () => {
            EventSystem.unsubscribe('ScheduleControlModule', showMessage);
            window.removeEventListener('resize', debouncedResize);
        };
    }, []);

    function screenResized() {
        if (!scheduleDisplayRef.current) return;
        addLogEntry('Screen resized: ' + scheduleDisplayRef.current.clientHeight + '/' + scheduleDisplayRef.current.clientWidth);
        const h = parameters.containerHeight.container - 2;
        const w = parseInt(scheduleDisplayRef.current.clientWidth - 10, 10);
        setContainerHeight(h);
        setContainerWidth(w);
        liveRef.current.containerHeight = h;
        liveRef.current.containerWidth = w;
        showMessage(liveRef.current.lastDataMessage);
    }

    function showMessage(data) {
        if (parameters.config.event_grouping_seq !== data.event_grouping_seq) return;
        liveRef.current.lastDataMessage = data;

        const { containerHeight: ch, containerWidth: cw } = liveRef.current;
        const headingHeight = ch * 0.1;
        const maxBodyHeight = ch - headingHeight - 18;
        const nowHeight = maxBodyHeight * 0.6;
        const otherHeight = maxBodyHeight - nowHeight;

        const colourMap = { success: '#27aa74', primary: '#3f7cac', warning: '#f7bf3d', danger: '#ec0b43', info: '#4cc8e8' };
        const headingColour = colourMap[parameters.config.colour] ?? 'white';

        const headingCanvasCTX = headingCanvasRef.current.getContext('2d');
        const fontSize = getBoldFontSize(parameters.config.name.toUpperCase(), headingCanvasCTX, parseInt(cw, 10), parseInt(ch, 10) * 0.1);
        displayCanvasText(headingCanvasCTX, 0, 0, parseInt(cw, 10), parseInt(ch, 10) * 0.1, '#333333', 'black', 1,
            parameters.config.name.toUpperCase(), headingColour, 'center',
            'bold ' + fontSize + 'px montserrat', cw / 2, parseInt(ch, 10) * 0.05);

        const oc = parameters.config.other_config;
        const nowFontSize = getBoldFontSize(oc.currentItemLabel.toUpperCase(), headingCanvasCTX, nowHeight * 0.95, cw * 0.1);
        let nextFontSize = 0;
        let nowNextFontSize = 0;
        if (oc.nextItemLabel.length > 0) nextFontSize = getBoldFontSize(oc.nextItemLabel.toUpperCase(), headingCanvasCTX, otherHeight * 0.65, cw * 0.1);
        if (oc.nextNextItemLabel.length > 0) nowNextFontSize = getBoldFontSize(oc.nextNextItemLabel.toUpperCase(), headingCanvasCTX, otherHeight * 0.65, cw * 0.1);

        const sectionLabelFontSize = Math.min(nowFontSize, nextFontSize, nowNextFontSize);

        const bodyCanvasCTX = bodyCanvasRef.current.getContext('2d');
        displayBlock(bodyCanvasCTX, 0, 0, cw, nowHeight, cw * 0.1, sectionLabelFontSize, oc.currentItemLabel.toUpperCase(), data.nowNextLater[0], 1);
        if (oc.nextItemLabel.length > 0) displayBlock(bodyCanvasCTX, 0, nowHeight, cw / 2, otherHeight, cw * 0.1, sectionLabelFontSize, oc.nextItemLabel.toUpperCase(), data.nowNextLater[1], 2);
        if (oc.nextNextItemLabel.length > 0) displayBlock(bodyCanvasCTX, cw / 2, nowHeight, cw / 2, otherHeight, cw * 0.1, sectionLabelFontSize, oc.nextNextItemLabel.toUpperCase(), data.nowNextLater[2], 3);
    }

    function displayBlock(ctx, xOffset, yOffset, blockWidth, blockHeight, headingWidth, headingFontSize, heading, data, iteration) {
        ctx.clearRect(xOffset, yOffset, blockWidth, blockHeight);
        let headingHeight = blockHeight;
        let headingCenter = blockHeight / 2;
        if (iteration > 1) {
            headingHeight = blockHeight * 0.9;
            headingCenter = (headingHeight / 2) + (blockHeight * 0.05);
        }

        ctx.translate(xOffset, yOffset + blockHeight);
        ctx.rotate(-90 * Math.PI / 180);
        ctx.fillStyle = '#BFC9CA';
        ctx.fillRect(0, 0, headingHeight, headingWidth);
        ctx.textBaseline = 'middle';
        ctx.textAlign = 'center';
        ctx.font = ' ' + headingFontSize + 'px montserrat';
        ctx.fillStyle = '#333333';
        ctx.fillText(heading, headingCenter, headingWidth / 2);
        ctx.rotate(90 * Math.PI / 180);
        ctx.translate(-xOffset, -(yOffset + blockHeight));

        if (!data || Object.entries(data).length === 0) return;

        const { line1, line2, line3 } = data;
        const runTime = moment(data.runTime).format('h:mma');

        if (iteration > 1) {
            const heightRunTime = blockHeight * 0.2;
            const runTimeFontSize = getFontSize(runTime, ctx, headingWidth * 0.9, heightRunTime);
            ctx.font = runTimeFontSize + 'px montserrat';
            ctx.fillStyle = '#333333';
            ctx.fillText(runTime, xOffset + (headingWidth / 2), (blockHeight * 0.9) + yOffset);
        }

        const line1Split = split(line1);
        ctx.textBaseline = 'alphabetic';
        ctx.textAlign = 'left';
        const line1AFontSize = getFontSize(line1Split[0], ctx, blockWidth - headingWidth - 5, blockHeight * 0.3);
        const line1BFontSize = getFontSize(line1Split[1], ctx, blockWidth - headingWidth - 5, blockHeight * 0.3);
        const line1FontSize = Math.min(line1AFontSize, line1BFontSize);
        ctx.font = line1FontSize + 'px montserrat';
        ctx.fillStyle = 'white';
        ctx.fillText(line1Split[0], headingWidth + xOffset + 5, (blockHeight * 0.3) + yOffset);
        ctx.fillText(line1Split[1], headingWidth + xOffset + 5, (blockHeight * 0.6) + yOffset);

        const line2FontSize = Math.min(getFontSize(line2, ctx, blockWidth - headingWidth - 5, blockHeight * 0.18), line1FontSize);
        ctx.font = line2FontSize + 'px montserrat';
        ctx.fillStyle = '#BFC9CA';
        ctx.fillText(line2, headingWidth + xOffset + 5, (blockHeight * 0.78) + yOffset);

        const line3FontSize = Math.min(getFontSize(line3, ctx, blockWidth - headingWidth - 5, blockHeight * 0.19), line2FontSize);
        ctx.font = line3FontSize + 'px montserrat';
        ctx.fillStyle = '#BFC9CA';
        ctx.fillText(line3, headingWidth + xOffset + 5, (blockHeight * 0.95) + yOffset);
    }

    function split(line) {
        const lineArray = line.split(' ');
        if (lineArray.length === 1) return [line, ''];
        if (lineArray.length === 2) return [lineArray[0], lineArray[1]];

        const charCount = line.length - lineArray.length + 1;
        let runningCount = 0, halfwayCount = 0;
        let option1A = '', option1B = '', option2A = '', option2B = '';

        for (let x = 0; x < lineArray.length; x++) {
            runningCount += lineArray[x].length;
            if (runningCount < charCount / 2) {
                option1A += (x > 0 ? ' ' : '') + lineArray[x];
                option2A += (x > 0 ? ' ' : '') + lineArray[x];
            } else {
                if (halfwayCount === 0) {
                    option1A += (x > 0 ? ' ' : '') + lineArray[x];
                    option2B = lineArray[x];
                    halfwayCount = x;
                } else {
                    option1B += (option1B.length > 0 ? ' ' : '') + lineArray[x];
                    option2B += (option1B.length > 0 ? ' ' : '') + lineArray[x];
                }
            }
        }

        const diff1 = Math.abs(option1A.length - option1B.length);
        const diff2 = Math.abs(option2A.length - option2B.length);
        return diff1 < diff2 ? [option1A, option1B] : [option2A, option2B];
    }

    function displayCanvasText(ctx, x, y, width, height, boxFillColour, boxOutlineColour, boxOpacity, displayText, textColor, textAlign, font, textX, textY) {
        ctx.fillStyle = boxFillColour;
        ctx.strokeStyle = boxOutlineColour;
        ctx.lineWidth = 0.5;
        ctx.clearRect(x, y, width, height);
        ctx.globalAlpha = boxOpacity;
        ctx.fillRect(x, y, width, height);
        ctx.fillStyle = textColor;
        ctx.textAlign = textAlign;
        ctx.font = font;
        ctx.globalAlpha = 1;
        ctx.textBaseline = 'middle';
        ctx.fillText(displayText, textX, height / 2);
    }

    function getBoldFontSize(messageText, canvas, width, height) {
        let fontsize = height;
        do { fontsize--; canvas.font = 'bold ' + fontsize + 'px montserrat'; } while (canvas.measureText(messageText).width > width);
        return fontsize;
    }

    function getFontSize(messageText, canvas, width, height) {
        let fontsize = height;
        do { fontsize--; canvas.font = fontsize + 'px montserrat'; } while (canvas.measureText(messageText).width > width);
        return fontsize;
    }

    const ch = parseInt(containerHeight, 10);
    const cw = parseInt(containerWidth, 10);
    const headingHeight = ch * 0.1;
    const bodyHeight = ch - headingHeight - 18;
    const containerStyle = { position: 'absolute', top: '0px', left: '0px', width: '100%', height: parameters.containerHeight.container - 2 + 'px' };

    return (
        <Tile ref={scheduleDisplayRef} style={containerStyle}>
            <Body>
                <canvas width={cw - 10} height={headingHeight} style={{ paddingTop: '5px' }} ref={headingCanvasRef} />
                <canvas width={cw} height={bodyHeight} ref={bodyCanvasRef} />
            </Body>
            <div style={{ position: 'fixed', top: '-2000px', left: '-2000px', backgroundColor: 'pink', whiteSpace: 'pre' }}
                ref={fontSizeCheckerRef} />
        </Tile>
    );
}
